import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useState } from 'react';

const registerOnboarding = async ({ payload, token }) => {
  const response = await apiClient.post(
    '/emaratax/register',
    { ...payload },
    {
      headers: {
        'x-marmin-token': token,
      },
    },
  );
  return response.data;
};

export const useRegisterOnboardingHandler = ({ onSuccess, onError, token }) => {
  const dispatch = useDispatch();
  const { snackbarMessages } = useTranslatedMessages();
  const applicantMessages = snackbarMessages.business_profiles_applicant ?? {};
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: (payload) => registerOnboarding({ payload, token }),
    onSuccess: (data) => {
      dispatch(showSnackbar({ message: applicantMessages.onboarding_success, severity: 'success' }));
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      onError && onError();
      if (error instanceof AxiosError && error.response) {
        const { status, data } = error.response;
        if (status >= 400 && status < 500) {
          if (status === 400) {
            setError(data.errors);
            return;
          }
          const message =
            data?.errors?.message ||
            data?.message ||
            'Failed to onboard. Please check the provided information and try again.';
          dispatch(showSnackbar({ message, severity: 'error' }));
        } else {
          dispatch(
            showSnackbar({
              message:
                data?.errors?.message ||
                data?.message ||
                error?.message ||
                applicantMessages.applicant_verification_error ||
                'An error occurred during verification',
              severity: 'error',
            }),
          );
        }
      } else {
        dispatch(
          showSnackbar({
            message:
              error?.message ||
              applicantMessages.applicant_verification_error ||
              'An error occurred during verification',
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (payload) => {
    mutation.mutate(payload);
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
